import {
  useEffect,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { APP_BASE, Link, navigate, usePath, useSearch } from "./router";
import { useHermes, request, errorDetails } from "./lib/store";
import { ErrorNotice, Loading } from "./components";
import {CheckoutExperience} from "./checkout-experience";
import {
  COMMERCE,
  commerceProduct,
  commerceAmount,
  type CommerceProduct,
  type CommerceWorkspace,
  type CommerceOffer,
  type CommerceOrder,
  type CommerceCheckout,
  type CommercePolicy,
} from "../src/commerce-types";
import {DomainSearch,RegisteredDomains,DomainAdministration} from "./domains";
import {EARNINGS_PATH} from '../src/commerce-earnings-types';
import {PAYMENT_EXAMPLE_LABEL} from "../src/product";
import type { ErrorDetails } from "./lib/types";
import { CRYPTO_ASSETS } from "../src/commerce-crypto/assets";
import "./commerce.css";
import {useRequestIntent as useIntent} from './lib/request-intent';
import {MerchantManagement} from './merchant-card-management';
import {MerchantCardRefunds} from './merchant-card-refunds';
import {PaymentNotifications,PaymentNotificationFailures} from './payment-notifications';

const descriptions = {
  payments: [
    "A checkout that belongs to your product",
    "Create a price and a shareable checkout. Your app can read the result through a small API.",
    "Clear records, from payment to refund",
    "Products, orders, and refunds share one history. Existing EightBots subscriptions and AI credits keep their own purpose.",
  ],
  crypto: [
    "Payment infrastructure, without the extra app",
    "Create checkout links and track payments from checkout to receipt.",
    "Made for agent-built services",
    "Your agents can create checkout links and read order status through the EightBots payment API.",
  ],
} as const;
function PreviewNotice({ product }: { product?: CommerceProduct | null }) {
  if(product==='domains')return null;
  return (
    <div className="commerce-notice">
      <span className="commerce-badge">Preview</span>
      <p>
        Explore the payment flow. No money moves and no crypto is sent.
      </p>
    </div>
  );
}
function ProductNav({ account = false }: { account?: boolean }) {
  return (
    <nav className="commerce-product-nav" aria-label="Commerce products">
      {COMMERCE.products.map((p) => (
        <Link
          key={p.id}
          href={`${APP_BASE}${account ? p.accountPath : p.publicPath}`}
          aria-current={
            window.location.pathname ===
            `${APP_BASE}${account ? p.accountPath : p.publicPath}`
              ? "page"
              : undefined
          }
        >
          {p.name}
        </Link>
      ))}
      {account && (
        <Link href={`${APP_BASE}/account/commerce`}>All activity</Link>
      )}
    </nav>
  );
}
export function CommerceProductsPage() {
  return (
    <div className="commerce commerce-public">
      <header className="commerce-hero">
        <p className="commerce-eyebrow">EightBots for builders</p>
        <h1>
          Make something.
          <br />
          Make it a business.
        </h1>
        <p>
          Payments and domains for the apps your agents create. Three focused
          products, one place to manage them.
        </p>
      </header>
      <div className="commerce-product-grid">
        {COMMERCE.products.map((p) => (
          <article className="commerce-product-card" key={p.id}>
            <h2>{p.name}</h2>
            <p>{p.description}</p>
            <Link href={`${APP_BASE}${p.publicPath}`}>Explore {p.name}</Link>
          </article>
        ))}
      </div>
      <section className="commerce-statement">
        <h2>
          One account.
          <br />
          One clear record.
        </h2>
        <p>
          Your business payments and domain orders belong together. Your
          EightBots subscription and AI credits remain visible in Billing.
        </p>
      </section>
    </div>
  );
}
export function CommerceProductPage({ product }: { product: CommerceProduct }) {
  const p = commerceProduct(product),
    copy = product === "domains" ? null : descriptions[product];
  return (
    <div className="commerce commerce-public">
      <div className={`commerce-hero-grid${product==='domains'?' commerce-domain-intro':''}`}>
        <header className="commerce-hero">
          <h1>{p.headline}</h1>
          <p>{p.description}</p>
          <div className="commerce-actions">
            <a className="button" href={`${APP_BASE}${product === "domains" ? p.publicPath + "#domain-search" : p.publicPath + "/demo"}`}>
              {product === "domains" ? "Find a domain" : PAYMENT_EXAMPLE_LABEL}
            </a>
            {copy && <a className="commerce-text-link" href="#how-it-works">
              How it works
            </a>}
          </div>
        </header>
        {product !== "domains" && <div
          className={`commerce-flow-art commerce-art-${product}`}
          aria-label="Product flow illustration"
        >
          <span className="commerce-art-label">
            From your app to your customer
          </span>
          <div className="commerce-flow-item">
            <strong>
              Create a payment request
            </strong>
          </div>
          <div className="commerce-flow-item">
            <strong>
              Your customer checks out
            </strong>
          </div>
          <div className="commerce-flow-item">
            <strong>
              Your app gets the result
            </strong>
          </div>
          <span className="commerce-art-foot">
            {product === "crypto"
              ? "In-house infrastructure. A small, focused API."
              : "Built into EightBots."}
          </span>
        </div>}
      </div>
      {product === "domains" && <div id="domain-search"><DomainSearch /></div>}
      {copy && <section id="how-it-works" className="commerce-features">
        <div>
          <p className="commerce-eyebrow">Designed to fit your work</p>
          <h2>{copy[0]}</h2>
          <p>{copy[1]}</p>
        </div>
        <div>
          <p className="commerce-eyebrow">One source of truth</p>
          <h2>{copy[2]}</h2>
          <p>{copy[3]}</p>
        </div>
      </section>}
      {product !== "domains" && <section className="commerce-launch-note">
        <h2>Try a checkout example.</h2>
        <p>
          Create products and share checkout links from your account. Your payment page shows current availability and controls. Seller payouts are not available yet.
        </p>
        <PreviewNotice product={product} />
        <a className="button" href={`${APP_BASE}${p.publicPath}/demo`}>
          {PAYMENT_EXAMPLE_LABEL}
        </a>
      </section>}
    </div>
  );
}
type Actions = ReturnType<typeof useHermes>["actions"];
type Mutation = (path: string, body: unknown) => Promise<unknown>;
function formData(event: FormEvent<HTMLFormElement>) {
  event.preventDefault();
  return new FormData(event.currentTarget);
}
const text = (f: FormData, k: string) => String(f.get(k) ?? "");
const date = (v: string) =>
  new Date(v).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
function State({ value }: { value: string }) {
  return <span className="commerce-state">{value.replaceAll("_", " ")}</span>;
}
function Empty({ children }: { children: ReactNode }) {
  return <p className="commerce-empty">{children}</p>;
}
function MerchantProfile({
  data,
  mutate,
  busy,
}: {
  data: CommerceWorkspace;
  mutate: Mutation;
  busy: boolean;
}) {
  const intent = useIntent();
  async function submit(e: FormEvent<HTMLFormElement>) {
    const f = formData(e),
      body = {
        displayName: text(f, "displayName"),
        supportEmail: text(f, "supportEmail"),
      };
    try {
      await mutate("/profile", { ...body, requestId: intent.id(body) });
      intent.clear();
    } catch {}
  }
  return (
    <section className="commerce-section">
      <div className="commerce-section-title">
        <h2>Your merchant profile</h2>
        <p>
          Your product name and support address. Shared by Payments and Crypto Payments.
        </p>
      </div>
      <form className="commerce-form" onSubmit={submit}>
        <label>
          Business or project name
          <input
            name="displayName"
            maxLength={100}
            minLength={2}
            required
            defaultValue={data.profile?.displayName ?? ""}
          />
        </label>
        <label>
          Customer support email
          <input
            name="supportEmail"
            type="email"
            required
            defaultValue={data.profile?.supportEmail ?? ""}
          />
        </label>
        <div>
          <button className="button" disabled={busy} type="submit">
            Save profile
          </button>
        </div>
      </form>
    </section>
  );
}
function Offers({
  product,
  data,
  mutate,
  busy,
}: {
  product: "payments" | "crypto";
  data: CommerceWorkspace;
  mutate: Mutation;
  busy: boolean;
}) {
  const intent = useIntent(),
    checkoutIntent = useIntent();
  const offers = data.offers.filter((o) => o.product === product),
    [version, setVersion] = useState(0),
    p = commerceProduct(product);
  async function submit(e: FormEvent<HTMLFormElement>) {
    const f = formData(e),
      raw = text(f, "price"),
      body = {
        product,
        name: text(f, "name"),
        description: text(f, "description"),
        ...(product === "crypto" ? { assetId: text(f, "assetId") } : {}),
        price:
          product === "payments" && /^\d+(\.\d{1,2})?$/.test(raw)
            ? `${raw.split(".")[0]}.${(raw.split(".")[1] ?? "").padEnd(2, "0")}`
            : raw,
        interval: text(f, "interval"),
      };
    try {
      await mutate("/offers", { ...body, requestId: intent.id(body) });
      intent.clear();
      setVersion((v) => v + 1);
    } catch {}
  }
  async function checkout(o: CommerceOffer) {
    try {
      const r = (await mutate("/checkouts", {
        offerId: o.id,
        requestId: checkoutIntent.id(o.id),
      })) as CommerceOrder;
      checkoutIntent.clear();
      if (r.checkoutPath) navigate(`${APP_BASE}${r.checkoutPath}`);
    } catch {}
  }
  return (
    <>
      <section className="commerce-section">
        <div className="commerce-section-title">
          <h2>
            {product === "crypto"
              ? "What are you charging for?"
              : "Your products"}
          </h2>
          <p>
            {product === "crypto"
              ? "Create an invoice template for your app or service. Select a supported asset and network. This preview collects no money."
              : "Create a price, publish it to your preview, and open the customer checkout."}
          </p>
        </div>
        {offers.length ? (
          <div className="commerce-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <strong>{o.name}</strong>
                      <small>{o.description}</small>
                    </td>
                    <td>
                      {commerceAmount(o.amountAtomic, o.currency, o.decimals)}
                      <small>
                        {o.interval === "once"
                          ? "One-time"
                          : `Per ${o.interval} · preview first payment only`}
                      </small>
                    </td>
                    <td>
                      <State value={o.state} />
                    </td>
                    <td>
                      <div className="commerce-row-actions">
                        {o.state === "draft" && (
                          <button
                            disabled={busy}
                            onClick={() =>
                              void mutate(`/offers/${o.id}/state`, {
                                state: "published",
                                requestId: crypto.randomUUID(),
                              }).catch(() => {})
                            }
                          >
                            Publish preview
                          </button>
                        )}
                        {o.state === "published" && (
                          <button
                            disabled={busy}
                            onClick={() => void checkout(o)}
                          >
                            Open checkout
                          </button>
                        )}
                        {o.state !== "archived" && (
                          <button
                            disabled={busy}
                            onClick={() =>
                              void mutate(`/offers/${o.id}/state`, {
                                state: "archived",
                                requestId: crypto.randomUUID(),
                              }).catch(() => {})
                            }
                          >
                            Archive
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>
            Your first product starts here. Nothing has been created yet.
          </Empty>
        )}
      </section>
      <section className="commerce-section">
        <h2>Create a product</h2>
        {!data.profile ? (
          <p>Save your merchant profile before creating products.</p>
        ) : (
          <form key={version} className="commerce-form" onSubmit={submit}>
            <label>
              Product name
              <input name="name" required minLength={2} maxLength={100} />
            </label>
            {product === "crypto" && (
              <label>
                Asset and network
                <select name="assetId">
                  {CRYPTO_ASSETS.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Price ({product === "crypto" ? "selected asset" : p.currency})
              <input
                name="price"
                inputMode="decimal"
                required
                pattern={
                  product === "crypto"
                    ? "[0-9]+([.][0-9]{1,18})?"
                    : "[0-9]+([.][0-9]{1,2})?"
                }
                placeholder="0.00"
              />
            </label>
            <label>
              Billing interval
              <select name="interval">
                <option value="once">One-time</option>
                {product === "payments" && (
                  <>
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                  </>
                )}
              </select>
            </label>
            <label className="commerce-full">
              Description
              <textarea name="description" maxLength={1000} rows={3} />
            </label>
            <div>
              <button className="button" disabled={busy}>
                Create draft
              </button>
            </div>
          </form>
        )}
      </section>
    </>
  );
}
function Orders({
  orders,
  mutate,
  busy,
}: {
  orders: CommerceOrder[];
  mutate?: Mutation;
  busy: boolean;
}) {
  return (
    <section className="commerce-section">
      <h2>Orders and payments</h2>
      <p>
        Preview records only. Completed simulations are not receipts for real
        payments.
      </p>
      {!orders.length ? (
        <Empty>
          No orders yet. Open a product checkout to explore the payment flow.
        </Empty>
      ) : (
        <div className="commerce-table-scroll">
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Amount</th>
                <th>State</th>
                <th>Created</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td>
                    <strong>{o.description}</strong>
                    <small>{o.id}</small>
                  </td>
                  <td>
                    {commerceAmount(o.amountAtomic, o.currency, o.decimals)}
                  </td>
                  <td>
                    <State value={o.state} />
                    {o.expiresAt &&
                      o.state === "open" &&
                      new Date(o.expiresAt) < new Date() && (
                        <small>Expired</small>
                      )}
                  </td>
                  <td>{date(o.createdAt)}</td>
                  <td>
                    {o.state === "completed" && mutate ? (
                      <button
                        disabled={busy}
                        onClick={() =>
                          void mutate(`/orders/${o.id}/refund`, {
                            requestId: crypto.randomUUID(),
                          }).catch(() => {})
                        }
                      >
                        Simulate full refund
                      </button>
                    ) : o.checkoutPath ? (
                      <Link href={`${APP_BASE}${o.checkoutPath}`}>
                        View checkout
                      </Link>
                    ) : (
                      <span>Domain request</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
function Integration({
  product,
  data,
  mutate,
  busy,
  editProfile,
}: {
  product: "payments" | "crypto";
  data: CommerceWorkspace;
  mutate: Mutation;
  busy: boolean;
  editProfile:()=>void;
}) {
  const [secret, setSecret] = useState<string | null>(null),
    [tokenError, setTokenError] = useState<string | null>(null),
    intent = useIntent();
  async function create(e: FormEvent<HTMLFormElement>) {
    const f = formData(e),
      body = { name: text(f, "name"), product, mode:text(f,"mode"), lifetime:text(f,"lifetime") };
    try {
      const r = (await mutate("/tokens", {
        ...body,
        requestId: intent.id(body),
      })) as { token: string | null };
      intent.clear();
      setSecret(r.token);
      setTokenError(null);
      if (!r.token)
        setTokenError(
          "This token was already created. Its secret is shown only once. Revoke it and create another if you did not save it.",
        );
    } catch {}
  }
  return (
    <>
      <section className="commerce-section">
        <h2>Connect your app</h2>
        <p>
          Keep your payment token on your app's server. It can list published products,
          create checkouts, read orders, and manage its own notification destination. A live Payments token can also create and manage subscriptions. It cannot issue refunds.
        </p>
        <pre className="commerce-code">{`POST /api/commerce/agent/checkouts\nAuthorization: Bearer YOUR_PAYMENT_TOKEN\nContent-Type: application/json\n\n{\n  "offerId": "YOUR_PRODUCT_ID",\n  "requestId": "A_UNIQUE_UUID_FOR_THIS_ORDER"\n}`}</pre>
        <p>
          Use the same request ID when retrying the same order. Open the
          returned <code>checkoutPath</code> on this site's origin. Read{" "}
          <code>GET /api/commerce/agent/orders/:id</code> before granting
          access. For a real payment, require <code>mode: live</code> and{" "}
          <code>order.fulfillmentReady: true</code>. A preview result is not proof of payment.
        </p>
        <p>
          Connect payment notifications below to receive signed order updates.
        </p>
        {product==='payments'&&<p>For monthly or yearly products, call <code>POST /api/commerce/agent/subscriptions</code> with the offer ID, request ID, your customer’s <code>buyerRef</code>, quantity and an HTTPS <code>returnUrl</code>.
          Read <code>GET /api/commerce/agent/subscriptions/:id</code> for confirmed paid access. A browser return does not confirm payment.</p>}
      </section>
      <PaymentNotifications data={data} product={product} mutate={mutate} busy={busy}/>
      <section className="commerce-section">
        <h2>Payment API tokens</h2>
        <p>Live tokens use real products. Preview tokens use examples. Creating a token does not enable payment collection.</p>
        {!data.profile&&<button className="button" type="button" onClick={editProfile}>Set up your merchant profile</button>}
        {tokenError && <ErrorNotice title="The payment API token could not be created" error={{name:'PaymentTokenError',message:tokenError}}/>}
        {secret && (
          <div className="commerce-secret">
            <label>
              Copy this token now. It is shown once.
              <textarea readOnly value={secret} rows={3} />
            </label>
            <button onClick={() => setSecret(null)}>I have saved it</button>
          </div>
        )}
        {data.tokens
          .filter((t) => t.product === product)
          .map((t) => (
            <div className="commerce-token-row" key={t.id}>
              <div>
                <strong>{t.name}</strong>
                <small>
                  {t.mode==='live'?'Live':'Preview'} · {t.prefix}… · {t.expiresAt?`Expires ${date(t.expiresAt)}`:'Valid until revoked'}
                </small>
              </div>
              {t.revokedAt ? (
                <State value="revoked" />
              ) : (
                <button
                  disabled={busy}
                  onClick={() =>
                    void mutate(`/tokens/${t.id}/revoke`, {
                      requestId: crypto.randomUUID(),
                    }).catch(() => {})
                  }
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
        {data.profile && (
          <form className="commerce-form" onSubmit={create}>
            <label>Mode<select name="mode" defaultValue="live">
              <option value="live">Live payments</option>
              <option value="preview">Preview examples</option>
            </select></label>
            <label>Token lifetime<select name="lifetime" defaultValue="30-days">
              <option value="30-days">30 days</option><option value="application">Until revoked — app server</option>
            </select></label>
            <label>
              Token name
              <input
                name="name"
                required
                minLength={2}
                maxLength={80}
                placeholder="My app backend"
              />
            </label>
            <div>
              <button className="button" disabled={busy}>
                Create token
              </button>
            </div>
          </form>
        )}
      </section>
    </>
  );
}
function DomainDetail({
  domain: d,
  mutate,
  busy,
}: {
  domain: CommerceWorkspace["domains"][number];
  mutate: Mutation;
  busy: boolean;
}) {
  return (
    <details className="commerce-domain">
      <summary>
        <strong>{d.domain}</strong>
        <State value={d.state} />
      </summary>
      <p>
        Saved in your EightBots account. Registration and automatic DNS setup
        are coming soon.
      </p>
      <dl className="commerce-details">
        <dt>DNS setup</dt>
        <dd>Automatic when registration launches</dd>
        <dt>Project in this account</dt>
        <dd>{d.applicationName || "Not selected"}</dd>
        <dt>Renewal reminders</dt>
        <dd>
          {d.renewalReminder ? "Requested for live launch" : "Not requested"}
        </dd>
        <dt>Renewal price</dt>
        <dd>Registrar quote required</dd>
      </dl>
      {d.state === "requested" && (
          <button
            className="commerce-text-link"
            disabled={busy}
            onClick={() =>
              void mutate(`/domains/${d.id}/cancel`, {
                requestId: crypto.randomUUID(),
              }).catch(() => {})
            }
          >
            Cancel saved choice
          </button>
      )}
    </details>
  );
}
export function CommerceAccountPage({
  product,
}: {
  product: CommerceProduct | null;
}) {
  const { state, actions } = useHermes(),
    [tab, setTab] = useState(product==='payments'||product==='crypto'?'sales':'overview');
  const search=useSearch(),registeringDomain=product==='domains'&&new URLSearchParams(search).has('domain');
  const resource = state.commerce,
    data = resource.data,
    busy = state.operation.label !== null;
  useEffect(() => {
    void actions.loadCommerce().catch(() => {});
  }, [actions, state.session.data?.accountId]);
  useEffect(() => setTab(product==='payments'||product==='crypto'?'sales':'overview'), [product]);
  const mutate: Mutation = (path, body) => actions.commerceMutation(path, body);
  const p = product ? commerceProduct(product) : null,
    policy = data?.policies.find((p) => p.product === product);
  return (
    <div className="commerce commerce-account">
      {!registeringDomain&&<ProductNav account />}
      {!registeringDomain&&<Link className="text-button" href={`${APP_BASE}${EARNINGS_PATH}`}>View earnings</Link>}
      {!registeringDomain&&<header className="commerce-account-heading">
        <p className="commerce-eyebrow">Your business tools</p>
        <h1>{p?.name ?? "Commerce activity"}</h1>
        <p>
          {product === "domains"
            ? "Choose a domain, set its registration term, and connect it to your app."
            : product === "crypto"
              ? "Create products and let customers pay from their wallets."
              : "Create, collect, and keep track of what you sell."}
        </p>
      </header>}
      {!((product==='payments'||product==='crypto')&&(tab==='sales'||tab==='integration'))&&<PreviewNotice product={product} />}
      <Loading
        active={resource.status === "loading" || resource.status === "idle"}
      />
      <ErrorNotice error={resource.error} />
      {data && (
        <>
          {product!=="domains" && tab!=='sales' && tab!=='integration' && policy && !policy.previewEnabled && (
            <p className="commerce-notice">
              This product preview is paused. Existing records remain available.
            </p>
          )}
          {product && product !== "domains" && (
            <nav className="commerce-tabs" aria-label="Product workspace">
              {["sales","overview","products","transactions","integration"].map(
                (t) => (
                  <button
                    key={t}
                    aria-current={tab === t ? "page" : undefined}
                    onClick={() => setTab(t)}
                  >
                    {({sales:'Payments',overview:'Preview overview',products:'Preview products',transactions:'Preview orders',integration:'Connect your app'} as Record<string,string>)[t]}
                  </button>
                ),
              )}
            </nav>
          )}
          {product === "domains" ? (
            <><p><Link href={`${APP_BASE}/account/invoices?product=domains`}>View invoices</Link></p><RegisteredDomains/>{data.domains.length>0&&<details className="commerce-section"><summary>Earlier saved choices</summary><p>These choices were saved before registration was available. They are not registered domains.</p>{data.domains.map(domain=><DomainDetail key={domain.id} domain={domain} mutate={mutate} busy={busy}/>)}</details>}</>
          ) : product === null ? (
            <>
              <Orders orders={data.orders} mutate={mutate} busy={busy} />
              <Activity events={data.events} />
            </>
          ) : (
            <>
              {(product==='payments'||product==='crypto')&&tab==='sales'&&<MerchantManagement method={product} hasProfile={data.profile!==null}
                profileEditor={<MerchantProfile key={data.profile?.updatedAt??'new'} data={data} mutate={mutate} busy={busy}/>}/>}
              {tab === "overview" && (
                <>
                  <div className="commerce-metrics">
                    {product === "crypto" ? (
                      <div>
                        <span>Completed invoices in latest orders</span>
                        <strong>
                          {
                            data.orders.filter(
                              (o) =>
                                o.product === "crypto" &&
                                o.state === "completed",
                            ).length
                          }
                        </strong>
                      </div>
                    ) : (
                      [
                        ["grossAtomic", "Preview gross receipts"],
                        ["refundedAtomic", "Preview refunds"],
                        ["netAtomic", "Preview net receipts"],
                      ].map(([key, label]) => {
                        const t = data.totals.find(
                          (t) => t.currency === p!.currency,
                        );
                        return (
                          <div key={key}>
                            <span>{label}</span>
                            <strong>
                              {commerceAmount(
                                t
                                  ? t[
                                      key as
                                        | "grossAtomic"
                                        | "refundedAtomic"
                                        | "netAtomic"
                                    ]
                                  : "0",
                                p!.currency,
                                p!.decimals,
                              )}
                            </strong>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <MerchantProfile
                    key={data.profile?.updatedAt ?? "new"}
                    data={data}
                    mutate={mutate}
                    busy={busy}
                  />
                  <section className="commerce-section">
                    <h2>Accept customer payments</h2>
                    <p>
                      Use the Payments tab for current availability, products and checkout links. The records on this overview are previews.
                    </p>
                    <p>
                      Preview fees:{" "}
                      {policy?.feeBasisPoints === null
                        ? "not configured"
                        : `${((policy?.feeBasisPoints ?? 0) / 100).toFixed(2)}%`}
                      . Preview calculations do not move money.
                    </p>
                  </section>
                </>
              )}
              {tab === "products" && (
                <Offers
                  product={product}
                  data={data}
                  mutate={mutate}
                  busy={busy || !policy?.previewEnabled}
                />
              )}{" "}
              {tab === "transactions" && (
                <Orders
                  orders={data.orders.filter((o) => o.product === product)}
                  mutate={mutate}
                  busy={busy || !policy?.previewEnabled}
                />
              )}{" "}
              {tab === "integration" && (
                <Integration
                  key={product}
                  product={product}
                  data={data}
                  mutate={mutate}
                  busy={busy}
                  editProfile={()=>setTab(product==='payments'||product==='crypto'?'sales':'overview')}
                />
              )}
            </>
          )}
          {tab!=='sales'&&data.hasMore && (
            <p>
              Showing the latest 100 orders and activity records. Totals include
              the complete preview journal.
            </p>
          )}
          <div className="commerce-billing-link">
            <Link href={`${APP_BASE}/billing`}>
              EightBots subscription and AI credits
            </Link>
            {product!=='domains'&&<p>Business receipts do not change your AI credit balance.</p>}
          </div>
        </>
      )}
    </div>
  );
}
function Activity({ events }: { events: CommerceWorkspace["events"] }) {
  return (
    <section className="commerce-section">
      <h2>Activity</h2>
      {events.length ? (
        <ol className="commerce-activity">
          {events.map((e) => (
            <li key={e.id}>
              <span>{e.kind.replaceAll(".", " · ").replaceAll("_", " ")}</span>
              <time>{date(e.createdAt)}</time>
            </li>
          ))}
        </ol>
      ) : (
        <Empty>No commerce activity yet.</Empty>
      )}
    </section>
  );
}
export function CommerceCheckoutPage() {
  const path = usePath(),
    token = path.split("/").at(-1)!;
  const [data, setData] = useState<CommerceCheckout | null>(null),
    [error, setError] = useState<ErrorDetails | null>(null),
    [pending, setPending] = useState(false);
  const intent = useIntent();
  useEffect(() => {
    let active = true;
    setPending(true);
    setData(null);
    setError(null);
    request<CommerceCheckout>(`/commerce/checkouts/${token}`)
      .then((d) => {
        if (active) setData(d);
      })
      .catch((e) => {
        if (active) setError(errorDetails(e));
      })
      .finally(() => {
        if (active) setPending(false);
      });
    return () => {
      active = false;
    };
  }, [token]);
  async function simulate(outcome: "completed" | "failed") {
    setPending(true);
    setError(null);
    try {
      await request(`/commerce/checkouts/${token}/simulate`, {
        body: { requestId: intent.id(outcome), outcome },
      });
      intent.clear();
      setData(await request(`/commerce/checkouts/${token}`));
    } catch (e) {
      setError(errorDetails(e));
      throw e;
    } finally {
      setPending(false);
    }
  }
  return (
    <div className="commerce commerce-checkout">
      <PreviewNotice />
      <Loading active={pending} />
      <ErrorNotice error={error} />
      {data && (
        <CheckoutPanel data={data} pending={pending} simulate={simulate}/>
      )}
    </div>
  );
}
export function CheckoutPanel(props: {
  data:CommerceCheckout;pending:boolean;simulate:(outcome:"completed"|"failed")=>void|Promise<void>;demo?:boolean;
}) {
  return <CheckoutExperience {...props}/>;
}

function PolicyForm({
  policy,
  actions,
  busy,
  owner,
  enabled,
}: {
  enabled:boolean;
  policy: CommercePolicy;
  actions: Actions;
  busy: boolean;
  owner: boolean;
}) {
  async function submit(e: FormEvent<HTMLFormElement>) {
    const f = formData(e);
    await actions
      .commerceMutation(
        `/policies/${policy.product}`,
        {
          previewEnabled: f.has("enabled"),
          feeBasisPoints:
            text(f, "percent") === ""
              ? null
              : Math.round(Number(text(f, "percent")) * 100),
          feeFixedMinor:
            text(f, "fixed") === ""
              ? null
              : Math.round(Number(text(f, "fixed")) * 100),
          reason: text(f, "reason"),
        },
        true,
      )
      .catch(() => {});
  }
  return (
    <section className="commerce-section">
      <h2>{commerceProduct(policy.product).name}</h2>
      <dl className="commerce-details">
        <dt>Connection</dt>
        <dd>{policy.productName}</dd>
        <dt>{policy.product==="domains"?"Registration":"Payment collection"}</dt>
        <dd>{enabled?"Enabled":"Not enabled"}</dd>
        <dt>Preview</dt>
        <dd>{policy.previewEnabled ? "Available" : "Paused"}</dd>
      </dl>
      {owner && (
        <form className="commerce-form" onSubmit={submit}>
          <label className="commerce-check commerce-full">
            <input
              type="checkbox"
              name="enabled"
              defaultChecked={policy.previewEnabled}
            />
            Enable this product preview
          </label>
          <label>
            {policy.product==="domains"?"Example fee (%)":"Seller fee (%)"}
            <input
              name="percent"
              type="number"
              step="0.01"
              min={0}
              max={100}
              defaultValue={
                policy.feeBasisPoints === null
                  ? ""
                  : (policy.feeBasisPoints / 100).toFixed(2)
              }
              placeholder="Not configured"
            />
          </label>
          <label>
            {policy.product==="domains"?"Fixed example fee (USD)":"Fixed seller fee (USD)"}
            <input
              name="fixed"
              type="number"
              step="0.01"
              min={0}
              max={1000}
              defaultValue={
                policy.feeFixedMinor === null
                  ? ""
                  : (policy.feeFixedMinor / 100).toFixed(2)
              }
              placeholder="Not configured"
            />
          </label>
          <label className="commerce-full">
            Reason for this change
            <input name="reason" minLength={3} maxLength={1000} required />
          </label>
          <div>
            <button className="button" disabled={busy}>
              Save service policy
            </button>
          </div>
        </form>
      )}
      <p>
        {policy.product==="domains"?"Domain prices come from each registration quote. These settings control the saved domain example.":"Seller fees apply to new checkouts. They are deducted from seller earnings and are not added to the customer’s price. Saved checkout terms stay unchanged."}
      </p>
    </section>
  );
}
export function CommerceAdminPage() {
  const { state, actions } = useHermes();
  useEffect(() => {
    void actions.loadAdminCommerce().catch(() => {});
  }, [actions, state.session.data?.accountId]);
  const r = state.adminCommerce,
    d = r.data;
  return (
    <div className="commerce commerce-account">
      <header className="commerce-account-heading">
        <p className="commerce-eyebrow">Backoffice</p>
        <h1>Commerce</h1>
        <p>
          Manage merchant payments, refunds, and domain registrations.
        </p>
      </header>
      <Loading active={r.status === "loading" || r.status === "idle"} />
      <ErrorNotice error={r.error} />
      <DomainAdministration/>
      <MerchantCardRefunds/>
      {d && (
        <>
          <PaymentNotificationFailures data={d.notificationFailures}/>
          <h2>Payment examples</h2>
          <PreviewNotice />
          <div className="commerce-metrics">
            {Object.entries(d.counts).map(([k, v]) => (
              <div key={k}>
                <span>{k}</span>
                <strong>{v}</strong>
              </div>
            ))}
          </div>
          {d.policies.map((p) => (
            <PolicyForm
              key={p.product + p.updatedAt}
              policy={p}
              enabled={d.availability[p.product].enabled}
              actions={actions}
              busy={state.operation.label !== null}
              owner={state.session.data?.adminRole === "owner"}
            />
          ))}
          <section className="commerce-section">
            <h2>Merchants</h2>
            {d.merchants.length ? (
              <div className="commerce-table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Account</th>
                      <th>Support</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.merchants.map((m) => (
                      <tr key={m.accountId}>
                        <td>{m.displayName}</td>
                        <td>
                          <Link
                            href={`${APP_BASE}/admin/accounts/${m.accountId}`}
                          >
                            {m.username}
                          </Link>
                        </td>
                        <td>{m.supportEmail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty>No merchant profiles have been created.</Empty>
            )}
          </section>
          <Orders orders={d.orders} busy={false} />
          <Activity events={d.events} />
          {d.hasMore && (
            <p>
              Showing the latest 100 records. Counts include all preview
              records.
            </p>
          )}
          <section className="commerce-section">
            <h2>Launch requirements</h2>
            <p>
              Payments: complete refund recovery, disputes, application payment results, and a paid checkout review. Seller payouts are deferred.
            </p>
            <p>
              Crypto: verify a funded payment, its tax record, invoice, refund, and app delivery.
            </p>
            <p>
              Domains: fund the registrar account to buy domains. New registrations use customer credits. Automatic DNS and HTTPS setup runs after registration when the customer selects an app. Automatic domain renewal is off.
            </p>
            <p>
              Account payment pages show current availability and any restrictions.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
